import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
} from 'antd';
import { createStyles } from 'antd-style';
import { useSearchParams } from 'react-router-dom';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
// import WoodModal from './WoodModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

import { HPOOTHER } from "../../../../types/HPO";


import AddPOOther from '../purchase-order-products/add-po-other';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

const PurchaseOrder: React.FC = () => {
  const [searchParams] = useSearchParams();
  const categoryType = searchParams.get('type');

  const [hpurchaseorders, setHPO] = useState<HPOOTHER[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<any | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchHPO = async (page = 1, limit = 10) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        bill_type: categoryType ? categoryType : '',
      });

      const data = await apiRequest<HPOOTHER[]>({
        method: 'get',
        url: `get-hpo?${params.toString()}`,
      });
      setHPO(data);
    } catch (error) {
      showError('Failed to fetch HPOOTHER');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchHPO();
  }, []);

  const handleEditClick = async (record: HPOOTHER) => {
    try {
      const response = await apiRequest<any>({
        method: 'get',
        url: `/get-po-bill?id=${record.pbill_id}`
      });

      if (!response) {
        message.error('Failed to load bill details');
        return;
      }

      setIsEditMode(true);
      setInitialValues({
        pbill_id: response.pbill_id,
        ledger_id: response.pbill_ledger_id,
        bill_no: response.pbill_bill_no,
        challan_no: response.pbill_challan_no,
        pbill_po_no: response.pbill_po_no,
        bill_date: response.pbill_bill_date ? dayjs(response.pbill_bill_date, 'DD-MM-YYYY') : null,
        motor_vehicle_no: response.pbill_motor_vehicle_no,
        freight_outward: response.pbill_fright_amount,
        po_details: response.po_details || []
      } as any);
      setIsModalVisible(true);
    } catch (error) {
      console.error(error);
      message.error('Error loading bill details');
    }
  };

  const handleCreate = () => {
    fetchHPO();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pbill_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-hpo/${pbill_id}`,
      });
      showSuccess('HPOOTHER deleted');
      fetchHPO();
    } catch {
      showError('Failed to delete HPOOTHER');
    }
  };

  const handleStatusToggle = async (widthId: number) => {
    setHPO((prev) =>
      prev.map((item) =>
        item.pbill_id === widthId
          ? { ...item, pbill_status: item.pbill_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-hpo/${widthId}`,
      });
      showSuccess('HPOOTHER status updated');
    } catch (error) {
      showError('Failed to update status');
      setHPO((prev) =>
        prev.map((item) =>
          item.pbill_id === widthId
            ? { ...item, pbill_status: item.pbill_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<HPOOTHER>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };





  const columns: TableColumnsType<HPOOTHER> = [
    {
      title: 'Sr.',
      key: 'sr',
      width: 60,
      render: (_: any, __: HPOOTHER, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Bill Details',
      dataIndex: 'pbill_id',
      key: 'pbill_id',
      render: (_, record) => (
        <div>
          <div>
            <strong>Bill No.:</strong> {record.pbill_bill_no}<br />
            <strong>PO No.:</strong> {record.pbill_po_no || "—"}<br />
            <strong>Challan No.:</strong> {record.pbill_challan_no}<br />
            <strong>Bill Date:</strong> {record.pbill_bill_date}<br />
          </div>
        </div>
      ),
    },
    {
      title: 'Company Name',
      dataIndex: 'ledger_company_name',
      key: 'ledger_company_name',
      render: (_, record) => <div>{record.ledger_company_name} (<strong>{record.ledger_gstin}</strong>)</div>,
    },
    {
      title: 'Created By',
      dataIndex: 'user_name',
      key: 'user_name',
      render: (_, record) => <div>{record.user_name} </div>,
    },
  ];



  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 300,
      fixed: 'right' as const,
      render: (_: any, record: HPOOTHER) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pbill_status}
                onChange={() => handleStatusToggle(record.pbill_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this HPOOTHER?"
              onConfirm={() => handleDelete(record.pbill_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }
  const formatModuleName = (str: string) =>
    str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');



  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Purchase - {formatModuleName(categoryType ? categoryType : '')}</Breadcrumb.Item>
        </Breadcrumb>
        <Flex gap="small">
          {currentModule?.right_add === 'Y' && (
            <Flex gap="small" align="center">
              <Button type="primary" onClick={showCreateModal}>
                Add Purchase
              </Button>
            </Flex>
          )}
        </Flex>



      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<HPOOTHER>
          tableLayout="fixed"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pbill_id"
          dataSource={hpurchaseorders.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}

          bordered
          pagination={false}
          scroll={{ y: 37 * 17, x: 'max-content' }}
          locale={{ emptyText: 'No Purchase order found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, hpurchaseorders.length)} of{' '}
                {hpurchaseorders.length}
              </span>
              <Pagination
                {...pagination}
                total={hpurchaseorders.length}
                showSizeChanger
                onChange={(page, size) => fetchHPO(page, size)}
              />
            </Flex>
          )}
        />
      </div>

      <AddPOOther
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default PurchaseOrder;